import { type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Alert, Button, EmptyState } from '@csa/ui';
import { api } from '../../lib/api.js';

/**
 * Attaching a managed credential to a request.
 *
 * The user picks a binding and says where it goes -- a header, a query
 * parameter, a bearer token, or the password half of basic auth. They never
 * see or type the value. The worker resolves it from Secrets Manager at
 * execution, injects it, and the redactor strips it from every log line and
 * audit record on the way back.
 *
 * This is why the console refuses a hand-typed `Authorization` header. If a
 * user could paste a token, that credential would live in the request
 * history, in the record an approver reads, and in whatever screenshot ends
 * up attached to a ticket.
 */

export interface SecretRef {
  readonly bindingId: string;
  readonly field: string;
  readonly injectAs: 'header' | 'query' | 'bearer' | 'basic-password';
  readonly name: string;
}

interface Binding {
  readonly id: string;
  readonly role: string;
  readonly description: string;
}

const INJECT_LABELS: Record<SecretRef['injectAs'], string> = {
  header: 'As a header',
  query: 'As a query parameter',
  bearer: 'As a bearer token',
  'basic-password': 'As the basic-auth password',
};

const CONTROL =
  'w-full rounded-md border border-line bg-surface px-2 py-1.5 text-xs text-body ' +
  'focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/40';

export function SecretBindingPicker({
  targetId,
  value,
  onChange,
}: {
  targetId: string | null;
  value: readonly SecretRef[];
  onChange: (refs: SecretRef[]) => void;
}): ReactElement {
  const { data, isPending } = useQuery({
    queryKey: ['target-bindings', targetId],
    enabled: targetId !== null,
    staleTime: 60_000,
    queryFn: () => api.get<{ bindings: Binding[] }>(`/api/targets/${targetId}/bindings`),
  });

  const bindings = data?.bindings ?? [];

  if (isPending) return <p className="text-sm text-muted">Loading credentials…</p>;

  if (bindings.length === 0) {
    return (
      <EmptyState
        title="No credentials are bound to this target"
        description="Requests will be sent unauthenticated. An administrator can bind a secret to this target."
      />
    );
  }

  const update = (index: number, patch: Partial<SecretRef>): void => {
    onChange(value.map((ref, i) => (i === index ? { ...ref, ...patch } : ref)));
  };

  return (
    <div className="flex flex-col gap-3">
      {value.map((ref, index) => {
        /* `bearer` and `basic-password` have a fixed position, so naming one
           is meaningless. The field is hidden rather than ignored, so nobody
           types into a box that does nothing. */
        const needsName = ref.injectAs === 'header' || ref.injectAs === 'query';

        return (
          <div
            key={index}
            className="grid grid-cols-1 items-center gap-2 rounded-md border border-line p-2 sm:grid-cols-[1.5fr_1fr_1fr_1fr_2.25rem]"
          >
            <select
              className={CONTROL}
              value={ref.bindingId}
              onChange={(event) => update(index, { bindingId: event.target.value })}
              aria-label="Credential"
            >
              {bindings.map((binding) => (
                <option key={binding.id} value={binding.id}>
                  {binding.description || `${binding.role} credential`}
                </option>
              ))}
            </select>

            <select
              className={CONTROL}
              value={ref.injectAs}
              onChange={(event) =>
                update(index, { injectAs: event.target.value as SecretRef['injectAs'] })
              }
              aria-label="Where to inject it"
            >
              {Object.entries(INJECT_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>

            <input
              className={`${CONTROL} font-mono`}
              value={ref.field}
              onChange={(event) => update(index, { field: event.target.value })}
              placeholder="/apiKey"
              aria-label="Field within the secret"
              spellCheck={false}
            />

            {needsName ? (
              <input
                className={`${CONTROL} font-mono`}
                value={ref.name}
                onChange={(event) => update(index, { name: event.target.value })}
                placeholder={ref.injectAs === 'header' ? 'X-Api-Key' : 'api_key'}
                aria-label="Header or parameter name"
                spellCheck={false}
              />
            ) : (
              <span className="text-xs text-subtle">No name needed</span>
            )}

            <Button
              variant="ghost"
              size="xs"
              iconOnly
              aria-label="Remove credential"
              onClick={() => onChange(value.filter((_, i) => i !== index))}
            >
              <TrashIcon className="size-4" aria-hidden />
            </Button>
          </div>
        );
      })}

      <Button
        variant="ghost"
        size="sm"
        className="self-start"
        /* Eight is the server's own cap on `secretRefs`. Enforcing it here
           too means the limit is visible before submission rather than as a
           validation failure after. */
        disabled={value.length >= 8}
        leadingIcon={<PlusIcon className="size-4" aria-hidden />}
        onClick={() =>
          onChange([
            ...value,
            { bindingId: bindings[0]!.id, field: '/apiKey', injectAs: 'header', name: 'X-Api-Key' },
          ])
        }
      >
        Attach a credential
      </Button>

      <Alert tone="info">
        The value is resolved when the request runs. It is never sent to your browser, written to a
        log, or stored in the audit trail.
      </Alert>
    </div>
  );
}
